import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'

import { getIsFetching as getIsFetchingProperty } from 'client/contexts/Property/selectors'

import RatingComments from 'client/components/RatingComments'
import RatingSubRatings from 'client/components/RatingSubRatings'
import RatingContent from 'client/components/RatingContent'
import Button from 'src/comps/Atom/Buttons/Button'
import { RatingsModal } from '../RatingsModal'

import styles from './styles.module.css'
import { PAGES, SECTIONS } from 'src/hooks/useKafkaEvent/constants'
import { useKafkaEvent } from 'src/hooks/useKafkaEvent'
import { useReviews } from 'src/hooks/useReviews'

interface IRatingProps {
  propertyId: number
  ratings?: {
    count?: number
    score?: number
    subScores?: Array<{
      name: string
      localizedName: string
      value: number
    }>
  }
}

const Rating = ({ ratings, propertyId }: IRatingProps) => {
  const { formatMessage } = useIntl()
  const isLoading = useSelector(getIsFetchingProperty)
  const [showAllRatings, setShowAllRatings] = useState(false)
  const { hasMoreThanInitial } = useReviews()

  const { sendEvent } = useKafkaEvent({
    page: PAGES.DETAIL,
    eventName: 'click_open_reviews_modal',
  })

  if (isLoading || !ratings || Object.keys(ratings).length === 0) {
    return (
      <h2 className={styles.title} data-testid="rating-title">
        {formatMessage({ id: 'global.reviews-from' })}
      </h2>
    )
  }

  const { count, score, subScores } = ratings

  function showModal() {
    sendEvent({ section: SECTIONS.REVIEWS })
    setShowAllRatings(true)
  }

  return (
    <>
      <h2 className={styles.title}>{formatMessage({ id: 'global.reviews-from' })}</h2>

      <section className={styles.reviewsLayout} data-testid="rating">
        <RatingContent score={score ?? 0} count={count ?? 0} />
        <div>
          <RatingSubRatings subScores={subScores ?? []} />
        </div>
        <div className={styles.comments}>
          <RatingComments />
          {hasMoreThanInitial && (
            <div className={styles.allReviewsBtn}>
              <Button
                size="m"
                rounded
                backgroundColor="grey8"
                borderColor="grey4"
                color="grey1"
                onClick={showModal}
                data-testid="read-all-reviews-button"
              >
                {formatMessage({ id: 'global.reviews.read-all' })}
              </Button>
            </div>
          )}
        </div>
      </section>
      <RatingsModal
        isOpen={showAllRatings}
        onClose={() => setShowAllRatings(false)}
        score={score ?? 0}
        count={count ?? 0}
        subScores={subScores ?? []}
        propertyId={propertyId}
      />
    </>
  )
}

export default Rating
